import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getChapterListFromSubjectBatch,
  getTopicListFromSubjectBatchChapter,
} from "utils/metadata-split/depedentApiFetch";
import { isEmpty } from "utils/helpers";
import { setReactFormValues } from "utils/metadata-split/helper";
import { areArraysEqual } from "pages/configue/constants";
import { setTopicSelectedValues } from "pages/exam-schedule/helper";
import { useLazyQuery } from "@apollo/client";
import myClassroomClient from "utils/graphql/myclassroom";
import {
  GET_CHAPTERS_BY_SUBJECT,
  GET_TOPICS_BY_SUBJECT_AND_CHAPTER,
} from "utils/graphql/services/exam-schedule/queries";
import OverflowTooltip from "./OverflowTooltip";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

type Topic = {
  chapterId: number;
  chapterName: string;
  id: number;
  name: string;
  label: string;
  value: string;
};

// Subject Selection Component
export const SubjectSelection = ({
  subjectNumber,
  subjectData,
  handlePayload,
  isCreate,
  globalClass,
  globalCourseIds
}) => {
  const [selectedChapters, setSelectedChapters] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [chaptersData, setChapterDate] = useState<
    { id: string; name: string }[]
  >([]);
  const [topicsData, setTopicsDate] = useState<Topic[]>([]);

  const [expandedAccordions, setExpandedAccordions] = useState<{courses: boolean, programs: boolean}>({
    courses: false,
    programs: false
  });
  
  const {
    subject_id,
    subject_name,
    chapter_list,
    topic_list,
    course_list = [],
    program_list = [],
    is_course_hierarchy,
    is_program_hierarchy,
  } = subjectData;
  const [isIntialFetch, setIsIntialFetch] = useState(false);
  const [
    getChaptersBySubject,
    { loading: loadingChap, error: errorChap, data: dataChap },
  ] = useLazyQuery(GET_CHAPTERS_BY_SUBJECT, {
    client: myClassroomClient,
    fetchPolicy: "no-cache",
  });
  const [
    getTopicDataBySubjectChapters,
    { loading: loadingTop, error: errorTop, data: dataTop },
  ] = useLazyQuery(GET_TOPICS_BY_SUBJECT_AND_CHAPTER, {
    client: myClassroomClient,
    fetchPolicy: "no-cache",
  });

  const onChapterChange = (e) => {
    setSelectedChapters(e.target.value);
  };
  const onTopicChange = (e) => {
    setSelectedTopics(e.target.value);
  };

  const handleAccordionChange = (panel: 'courses' | 'programs') => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  useEffect(() => {
    (async () => {
      const call1: any = await getChaptersBySubject({
        variables: { subjectIds: +subjectData.subject_id,
          classId:globalClass?.[0] || '',
          courseIds:(globalCourseIds || []).map(Number)
         },
      })
        .then((res) => {
          if (!isEmpty(res?.data?.getChaptersBySubject)) {
            setChapterDate(res?.data?.getChaptersBySubject);
          } else {
            setChapterDate([]);
          }
        })
        .catch(() => {
          setChapterDate([]);
        });
    })();
  }, [subject_id]);

  useEffect(() => {
    if (!isEmpty(chapter_list)) {
      setSelectedChapters(chapter_list);
    }
  }, [chapter_list]);

  useEffect(() => {
    setSelectedTopics(topic_list);
  }, [topic_list]);

  useEffect(() => {
    (async () => {
      if(!isEmpty(selectedChapters) || isCreate || isIntialFetch){
        const call1: any = await getTopicDataBySubjectChapters({
          variables: {
            subjectId: +subjectData.subject_id,
            chapterIds: selectedChapters,
            classId:globalClass?.[0]  || '',
            courseIds:(globalCourseIds || []).map(Number)
          },
        })
          .then((res) => {
            if (!isEmpty(res?.data?.getTopicsBySubjectAndChapter)) {
              // Transform the data to include label and value keys
              const transformedData = res?.data?.getTopicsBySubjectAndChapter.map((item: any) => ({
                ...item,
                label: `${item.name} (${item.chapterName})`,
                value: `${item.id}--${item.chapterId}`
              }));
              setTopicsDate(transformedData);
              setIsIntialFetch(true);
            } else {
              setTopicsDate([]);
              // setIsIntialFetch(true);
            }
          })
          .catch(() => {
            setTopicsDate([]);
            // setIsIntialFetch(true);
          });
      }
    })();
  }, [selectedChapters]);

  useEffect(() => {
    const filteredTopics = topicsData.filter((topic) => {
      if (selectedTopics.includes(topic.value)) {
        return topic;
      }
    });
    const filterPayloadData = filteredTopics?.map((chapTopic) => {
      let obj = {
        chapter_id: `${chapTopic?.chapterId}`,
        chapter_name: chapTopic?.chapterName,
        topic_id: `${chapTopic?.id}`,
        topic_name: chapTopic?.name,
        subject_id,
        subject_name,
        sort_order: 1,
      };
      return obj;
    });
    handlePayload(subject_id, filterPayloadData);
  }, [selectedTopics, topicsData]);

  useEffect(() => {
    if (isIntialFetch || isCreate) {
      let matchedVales = setTopicSelectedValues(
        topicsData,
        selectedTopics,
        "value"
      );
      let isEqual = areArraysEqual(selectedTopics, matchedVales);
      if (!isEqual) {
        setSelectedTopics(matchedVales);
      }
    }
  }, [topicsData]);

  const renderHierarchySection = () => {

    const hasCourses = course_list?.length > 0;
    const hasPrograms = program_list?.length > 0;

    if (!hasCourses && !hasPrograms) return null;

    return (
      <Box sx={{ mt: 2 }}>
        {hasCourses && (
          <Accordion
            expanded={expandedAccordions.courses}
            onChange={handleAccordionChange('courses')}
            sx={{
              borderRadius: "8px !important",
              mb: hasPrograms ? 2 : 0,
              "&:before": { display: "none" },
              boxShadow: "0 2px 4px rgba(63,81,181,0.1)",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#3f51b5" }} />}
              sx={{
                bgcolor: "#e8eaf6",
                borderRadius: "6px",
                minHeight: "48px !important",
                "&.Mui-expanded": {
                  minHeight: "48px !important",
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#3f51b5",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Courses
                </Typography>
                <Chip
                  label={course_list.length}
                  size="small"
                  sx={{
                    bgcolor: "#3f51b5",
                    color: "#ffffff",
                    height: "20px",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: "#f5f7fa", p: 2 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {course_list.map((course, index) => (
                  <Chip
                    key={`course-${index}`}
                    label={course}
                    size="small"
                    sx={{
                      bgcolor: "#ffffff",
                      color: "#3f51b5",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      border: "1px solid #c5cae9",
                      boxShadow: "0 1px 2px rgba(63,81,181,0.1)",
                    }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {hasPrograms && (
          <Accordion
            expanded={expandedAccordions.programs}
            onChange={handleAccordionChange('programs')}
            sx={{
              borderRadius: "8px !important",
              "&:before": { display: "none" },
              boxShadow: "0 2px 4px rgba(156,39,176,0.1)",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#9c27b0" }} />}
              sx={{
                bgcolor: "#f3e5f5",
                borderRadius: "6px",
                minHeight: "48px !important",
                "&.Mui-expanded": {
                  minHeight: "48px !important",
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#9c27b0",
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Programs
                </Typography>
                <Chip
                  label={program_list.length}
                  size="small"
                  sx={{
                    bgcolor: "#9c27b0",
                    color: "#ffffff",
                    height: "20px",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: "#faf5fb", p: 2 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {program_list.map((program, index) => (
                  <Chip
                    key={`program-${index}`}
                    label={program}
                    size="small"
                    sx={{
                      bgcolor: "#ffffff",
                      color: "#9c27b0",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      border: "1px solid #e1bee7",
                      boxShadow: "0 1px 2px rgba(156,39,176,0.1)",
                    }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  };

  return (
    <Card sx={{ mb: 3, bgcolor: "#f8f9fa", border: "1px solid #e0e0e0" }}>
      <CardContent>
        <Box
          sx={{
            mb: 1,
            pb: 1.5,
            borderBottom: "2px solid #e8eaf6",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#1a237e",
              letterSpacing: "0.5px",
            }}
          >
            Selected Subject {subjectNumber}
          </Typography>
          {/* ====== CHANGE 16: Hierarchy section now renders below title ====== */}
          {renderHierarchySection()}
        </Box>

        {/* Disabled Subject Field */}
        <TextField
          fullWidth
          label="Subject"
          value={subjectData?.subject_name || "Pre Filled"}
          disabled
          sx={{ mb: 3 }}
          placeholder="Pre Filled"
        />

        {/* Chapter and Topic Dropdowns */}
        <Box sx={{ display: "flex", gap: 3 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel shrink>Select Chapter</InputLabel>
            <Select
              multiple
              value={selectedChapters}
              onChange={onChapterChange}
              input={<OutlinedInput label="Select Chapter" />}
              MenuProps={MenuProps}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em style={{ color: "#9e9e9e" }}>Select chapter</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected?.map((value) => {
                      const chapter: any = chaptersData.find(
                        (ch: any) => ch?.id === value
                      );
                      return (
                        <Chip
                          key={value}
                          label={chapter?.name || value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                );
              }}
              displayEmpty
            >
              {chaptersData.length === 0 ? (
                <MenuItem disabled>No chapters available</MenuItem>
              ) : (
                chaptersData.map((chapter: any) => (
                  <MenuItem key={chapter.id} value={chapter.id}>
                    {chapter.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl sx={{ flex: 1 }}>
            <InputLabel shrink>Select Topic</InputLabel>
            <Select
              multiple
              value={selectedTopics}
              onChange={onTopicChange}
              input={<OutlinedInput label="Select Topic" />}
              MenuProps={MenuProps}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em style={{ color: "#9e9e9e" }}>Select topics</em>;
                }
                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => {
                      const topic: any = topicsData?.find(
                        (tp: any) => tp.value === value
                      );
                      return (
                        <Chip
                          key={value}
                          label={topic?.label || value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                );
              }}
              displayEmpty
            >
              {topicsData.length === 0 ? (
                <MenuItem disabled>No topics available</MenuItem>
              ) : (
                topicsData.map((topic: any) => (
                  <MenuItem key={topic.value} value={topic.value}>
                    <OverflowTooltip>{topic.label}</OverflowTooltip>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );
};
